// src/components/layout/SubmissionDetail.jsx
import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft,
  Calendar,
  Users,
  BookOpen,
  FileText,
  TrendingUp,
  Edit,
  Trash2,
  Calculator,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  BarChart3,
  Target,
  UserCheck,
  UserX,
  School,
  Hash,
  Award,
  Info
} from 'lucide-react';
import { AssessmentService } from '../../services/taskService'

const SubmissionDetail = ({ submission, onBack, onEdit, onRecordMarks, onViewMarks, onDelete }) => {
  const [task, setTask] = useState(null);
  const [marks, setMarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadSubmissionData();
  }, [submission.id]);

  const loadSubmissionData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load task details
      const taskData = await AssessmentService.getAssessmentTask(submission.taskId);
      setTask(taskData);

      // Load marks for this submission
      try {
        const marksData = await AssessmentService.getLearnerMarks(submission.taskId, submission.id);
        setMarks(marksData);
      } catch (marksError) {
        console.warn('Could not load marks:', marksError);
        setMarks([]);
      }

    } catch (err) {
      console.error('Error loading submission data:', err);
      setError('Failed to load submission details');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = () => {
    if (window.confirm(`Are you sure you want to delete this submission for "${task?.name}"?`)) {
      onDelete(submission.id, task?.name || 'Unknown Task');
    }
  };

  // Calculate statistics
  const submissionRate = submission.expectedSubmissions > 0 
    ? (submission.actualSubmissions / submission.expectedSubmissions) * 100 
    : 0;
  
  const nonSubmissions = submission.expectedSubmissions - submission.actualSubmissions - submission.absentees;
  const attendanceRate = submission.expectedSubmissions > 0 
    ? ((submission.expectedSubmissions - submission.absentees) / submission.expectedSubmissions) * 100 
    : 0;

  const hasMarks = marks.length > 0;
  const averageMark = hasMarks 
    ? marks.reduce((sum, mark) => sum + mark.percentageMark, 0) / marks.length 
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <AlertTriangle className="w-16 h-16 text-red-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Submission</h3>
        <p className="text-gray-500 mb-4">{error}</p>
        <button
          onClick={loadSubmissionData}
          className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-blue-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="text-blue-600 hover:text-blue-800 flex items-center gap-2 font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Submissions
          </button>
        </div>
        
        <div className="flex items-center gap-2">
            {hasMarks && (
                <button
                onClick={() => onViewMarks(submission)}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center gap-2"
                >
                <Calculator className="w-4 h-4" />
                Manage Marks ({marks.length})
                </button>
            )}
            
            {onRecordMarks && (
                <button
                onClick={() => onRecordMarks(submission)}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-2"
                >
                <Calculator className="w-4 h-4" />
                {hasMarks ? 'Add More Marks' : 'Record Marks'}
                </button>
            )}
          
          <button
            onClick={() => onEdit(submission)}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
          >
            <Edit className="w-4 h-4" />
            Edit Submission
          </button>
          
          <button
            onClick={handleDeleteClick}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Submission Overview */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-3 mb-6">
              <FileText className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">Submission Overview</h2>
            </div>
            
            <div className="space-y-4">
              {/* Task Information */}
              <div className="flex flex-wrap items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-gray-900">{task?.name}</span>
                </div>
                {task?.type && (
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    task.type === 'Exam' ? 'bg-red-100 text-red-800' :
                    task.type === 'Test' ? 'bg-orange-100 text-orange-800' :
                    task.type === 'Project' ? 'bg-purple-100 text-purple-800' :
                    task.type === 'Assignment' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {task.type}
                  </span>
                )}
                {task?.totalMarks && (
                  <div className="flex items-center gap-1 text-sm text-gray-600">
                    <Award className="w-4 h-4" />
                    <span>{task.totalMarks} marks</span>
                  </div>
                )}
              </div>

              {/* Submission Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Submission Date</p>
                      <p className="text-sm text-gray-600">{new Date(submission.date).toLocaleDateString('en-ZA', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <School className="w-4 h-4 text-gray-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Grade & Subject</p>
                      <p className="text-sm text-gray-600">{submission.grade} • {submission.subject}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Hash className="w-4 h-4 text-gray-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Term</p>
                      <p className="text-sm text-gray-600">{submission.term}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Clock className="w-4 h-4 text-gray-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Created</p>
                      <p className="text-sm text-gray-600">{new Date(submission.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    {hasMarks ? (
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                    ) : (
                      <XCircle className="w-4 h-4 text-orange-500" />
                    )}
                    <div>
                      <p className="text-sm font-medium text-gray-900">Marks Status</p>
                      <p className={`text-sm ${hasMarks ? 'text-green-600' : 'text-orange-600'}`}>
                        {hasMarks ? `Recorded for ${marks.length} learners` : 'Not recorded yet'}
                      </p>
                    </div>
                  </div>
                  
                  {hasMarks && (
                    <div className="flex items-center gap-3">
                      <TrendingUp className="w-4 h-4 text-gray-500" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Class Average</p>
                        <p className="text-sm text-gray-600">{averageMark.toFixed(1)}%</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Submission Statistics */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-3 mb-6">
              <BarChart3 className="w-6 h-6 text-green-600" />
              <h2 className="text-xl font-semibold text-gray-900">Submission Analysis</h2>
            </div>
            
            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <UserCheck className="w-5 h-5 text-green-600" />
                  <span className="text-2xl font-bold text-green-900">{submission.actualSubmissions}</span>
                </div>
                <p className="text-sm font-medium text-green-800">Submitted</p>
                <p className="text-xs text-green-600">{submissionRate.toFixed(1)}% of expected</p>
              </div>

              <div className="bg-red-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <UserX className="w-5 h-5 text-red-600" />
                  <span className="text-2xl font-bold text-red-900">{nonSubmissions}</span>
                </div>
                <p className="text-sm font-medium text-red-800">Missing</p>
                <p className="text-xs text-red-600">
                  {((nonSubmissions / submission.expectedSubmissions) * 100).toFixed(1)}% of expected
                </p>
              </div>

              <div className="bg-yellow-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <Users className="w-5 h-5 text-yellow-600" />
                  <span className="text-2xl font-bold text-yellow-900">{submission.absentees}</span>
                </div>
                <p className="text-sm font-medium text-yellow-800">Absent</p>
                <p className="text-xs text-yellow-600">
                  {((submission.absentees / submission.expectedSubmissions) * 100).toFixed(1)}% of expected
                </p>
              </div>
            </div>

            {/* Visual Progress Bars */}
            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">Submission Rate</span>
                  <span className="text-sm text-gray-600">{submissionRate.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className={`h-3 rounded-full transition-all duration-300 ${
                      submissionRate >= 90 ? 'bg-green-500' :
                      submissionRate >= 70 ? 'bg-yellow-500' :
                      'bg-red-500'
                    }`}
                    style={{ width: `${Math.min(submissionRate, 100)}%` }}
                  ></div>
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">Attendance Rate</span>
                  <span className="text-sm text-gray-600">{attendanceRate.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className={`h-3 rounded-full transition-all duration-300 ${
                      attendanceRate >= 95 ? 'bg-green-500' :
                      attendanceRate >= 85 ? 'bg-yellow-500' :
                      'bg-red-500'
                    }`}
                    style={{ width: `${Math.min(attendanceRate, 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-900 mb-1">Summary</h4>
                  <p className="text-sm text-blue-800">
                    Out of {submission.expectedSubmissions} expected learners, {submission.actualSubmissions} submitted their work
                    ({submissionRate.toFixed(1)}% submission rate). {submission.absentees} learners were absent, 
                    leaving {nonSubmissions} non-submissions to follow up on.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Topics Covered */}
          {submission.topics && submission.topics.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-3 mb-4">
                <Target className="w-6 h-6 text-purple-600" />
                <h2 className="text-xl font-semibold text-gray-900">Topics Covered</h2>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {submission.topics.map((topic, index) => (
                  <span
                    key={index}
                    className="px-3 py-2 bg-purple-100 text-purple-800 text-sm font-medium rounded-lg"
                  >
                    {topic}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Quick Actions & Stats */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Stats</h3>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Expected Submissions</span>
                <span className="font-semibold text-gray-900">{submission.expectedSubmissions}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Actual Submissions</span>
                <span className="font-semibold text-green-600">{submission.actualSubmissions}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Absentees</span>
                <span className="font-semibold text-yellow-600">{submission.absentees}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Non-submissions</span>
                <span className="font-semibold text-red-600">{nonSubmissions}</span>
              </div>
              
              <hr className="my-3" />
              
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Submission Rate</span>
                <span className={`font-semibold ${
                  submissionRate >= 90 ? 'text-green-600' :
                  submissionRate >= 70 ? 'text-yellow-600' :
                  'text-red-600'
                }`}>
                  {submissionRate.toFixed(1)}%
                </span>
              </div>
              
              {hasMarks && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Marks Recorded</span>
                    <span className="font-semibold text-blue-600">{marks.length}</span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Class Average</span>
                    <span className="font-semibold text-blue-600">{averageMark.toFixed(1)}%</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Task Information */}
          {task && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Task Details</h3>
              
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-700">Task Name</p>
                  <p className="text-sm text-gray-900">{task.name}</p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-gray-700">Type</p>
                  <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                    task.type === 'Exam' ? 'bg-red-100 text-red-800' :
                    task.type === 'Test' ? 'bg-orange-100 text-orange-800' :
                    task.type === 'Project' ? 'bg-purple-100 text-purple-800' :
                    task.type === 'Assignment' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {task.type}
                  </span>
                </div>
                
                {task.totalMarks && (
                  <div>
                    <p className="text-sm font-medium text-gray-700">Total Marks</p>
                    <p className="text-sm text-gray-900">{task.totalMarks}</p>
                  </div>
                )}
                
                {task.questions && (
                  <div>
                    <p className="text-sm font-medium text-gray-700">Questions</p>
                    <p className="text-sm text-gray-900">{task.questions.length}</p>
                  </div>
                )}
                
                <div>
                  <p className="text-sm font-medium text-gray-700">Created</p>
                  <p className="text-sm text-gray-900">{new Date(task.createdAt).toLocaleDateString()}</p>
                </div>
              </div>
            </div>
          )}

          {/* Action Items */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Action Items</h3>
            
            <div className="space-y-3">
              {!hasMarks && (
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className="w-4 h-4 text-orange-600" />
                    <span className="text-sm font-medium text-orange-800">Marks Pending</span>
                  </div>
                  <p className="text-xs text-orange-700">
                    No marks have been recorded for this submission yet.
                  </p>
                </div>
              )}
              
              {nonSubmissions > 0 && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <UserX className="w-4 h-4 text-red-600" />
                    <span className="text-sm font-medium text-red-800">Follow-up Required</span>
                  </div>
                  <p className="text-xs text-red-700">
                    {nonSubmissions} learner{nonSubmissions !== 1 ? 's' : ''} did not submit work.
                  </p>
                </div>
              )}
              
              {submissionRate < 70 && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="w-4 h-4 text-yellow-600" />
                    <span className="text-sm font-medium text-yellow-800">Low Submission Rate</span>
                  </div>
                  <p className="text-xs text-yellow-700">
                    Consider investigating reasons for low submission rate.
                  </p>
                </div>
              )}
              
              {submissionRate >= 90 && attendanceRate >= 95 && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-green-800">Excellent Performance</span>
                  </div>
                  <p className="text-xs text-green-700">
                    Great submission and attendance rates!
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmissionDetail;